import { useEffect, useMemo } from 'react';
import { EmptyState, Skeleton } from '@immo/ui-kit';
import {
  FLEET_HEALTH_WARN_PERCENT,
  type FleetHostHealth,
  type FleetHostHealthPoint,
  type FleetMachineOverview,
} from '@immo/api-contract';
import { useLoaderData } from 'react-router-dom';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ServerIcon } from '@/components/layout/icons';
import {
  floorTenth,
  formatFactor,
  formatGigabytes,
  isNearlyFull,
  limitsOf,
  loadOf,
  LOAD_BAR_SCALE,
  percentOf,
  ratioPercent,
  temperatureOf,
  type Figure,
} from '@/components/fleet/health/health-figures';
import type { ChartPoint } from '@/components/fleet/health/health-line-chart';
import { HistoryCard } from '@/components/fleet/health/history-card';
import { HistoryFilters } from '@/components/fleet/health/history-filters';
import { useHostHealthSeries } from '@/components/fleet/health/use-host-health-series';
import { rangeWindow, readSelection, todayKey, type HistoryRange } from '@/lib/fleet/history-range';
import { groupAndSort } from '@/lib/fleet/machine-order';
import { useListParams } from '@/lib/list-view/use-list-params';

/** One machine's health over a chosen period, as five line charts (IMM-1450). */
export function FleetHistoryPage() {
  const t = useTranslations('pages.admin.fleet-history');
  const tFleet = useTranslations('pages.admin.fleet');
  const data = useLoaderData() as ReadonlyArray<FleetMachineOverview>;
  const { params, setParams } = useListParams();
  const today = todayKey(new Date(Date.now()));

  const machines = useMemo(() => groupAndSort(data).machines, [data]);
  const first = machines[0];
  const requested = readSelection(params, today);
  const known = machines.some((machine) => machine.id === requested.machineId);
  const needsFallback = first !== undefined && !known;
  const firstId = first?.id;

  // An unknown or missing machine reads as the first one over 24 hours, and the address says so.
  useEffect(() => {
    if (needsFallback && firstId !== undefined) {
      setParams({ machine: firstId, range: '24h', from: null, to: null }, { replace: true });
    }
  }, [needsFallback, firstId, setParams]);

  const topbar = (
    <PageTopbar
      leading={
        <BreadcrumbLeading
          icon={ServerIcon}
          crumbs={[{ label: tFleet('title'), to: '/fleet' }, { label: t('title') }]}
        />
      }
    />
  );

  if (first === undefined) {
    return (
      <>
        {topbar}
        <div className="flex h-full min-h-0 items-center justify-center">
          <EmptyState
            icon={<ServerIcon className="h-8 w-8" aria-hidden />}
            title={tFleet('empty-heading')}
            description={tFleet('empty-body')}
          />
        </div>
      </>
    );
  }

  const selection = known
    ? { ...requested, machineId: requested.machineId as string }
    : { machineId: first.id, range: '24h' as const, from: null, to: null };
  const selectionKey = [selection.machineId, selection.range, selection.from ?? '', selection.to ?? ''].join('|');

  return (
    <>
      {topbar}
      <div className="h-full min-h-0 overflow-y-auto bg-canvas">
        <div className="flex flex-col gap-4 p-4 sm:p-6">
          <HistoryFilters
            machines={machines}
            selection={selection}
            today={today}
            onChange={(changes) => setParams(changes)}
          />
          <HistoryResult
            key={`history-${selectionKey}`}
            machineId={selection.machineId}
            range={selection.range}
            from={selection.from}
            to={selection.to}
          />
        </div>
      </div>
    </>
  );
}

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2';
const CARD_COUNT = 5;

/** Keyed by the selection, so a new selection starts at loading and a failed read never shows the old range's lines. */
function HistoryResult({
  machineId,
  range,
  from,
  to,
}: {
  readonly machineId: string;
  readonly range: HistoryRange;
  readonly from: string | null;
  readonly to: string | null;
}) {
  const t = useTranslations('pages.admin.fleet-history');
  const tHealth = useTranslations('pages.admin.fleet.health');
  // One `now` per selection: the hook refetches whenever the window's milliseconds change.
  const window = useMemo(() => rangeWindow(range, from, to, new Date(Date.now())), [range, from, to]);
  const { status, series } = useHostHealthSeries(machineId, window.from, window.to);

  if (status === 'failed') {
    return <p className="py-10 text-center text-base-sm text-text-tertiary">{tHealth('series-load-error')}</p>;
  }

  if (status === 'loading' || series === null) {
    return (
      <div className={GRID}>
        {Array.from({ length: CARD_COUNT }, (_, index) => (
          <Skeleton key={index} className={index === CARD_COUNT - 1 ? 'h-48 w-full sm:col-span-2' : 'h-48 w-full'} />
        ))}
      </div>
    );
  }

  if (series.points.length === 0) {
    return <p className="py-10 text-center text-base-sm text-text-tertiary">{t('no-readings')}</p>;
  }

  return (
    <Charts
      points={series.points}
      bucketMs={series.bucketMs}
      from={window.from}
      to={window.to}
      latest={series.latest}
    />
  );
}

function Charts({
  points,
  bucketMs,
  from,
  to,
  latest,
}: {
  readonly points: ReadonlyArray<FleetHostHealthPoint>;
  readonly bucketMs: number;
  readonly from: Date;
  readonly to: Date;
  readonly latest: FleetHostHealth | null;
}) {
  const t = useTranslations('pages.admin.fleet.health');
  const limits = limitsOf(latest);

  const series = (value: (point: FleetHostHealthPoint) => number | null): ChartPoint[] =>
    points.map((point) => ({ at: new Date(point.at), value: value(point) }));

  const usedOfTotal = (figure: ReturnType<typeof percentOf>) =>
    figure.kind === 'value'
      ? t('used-of-total', {
          used: t('gigabytes', { value: formatGigabytes(figure.usedBytes) }),
          total: t('gigabytes', { value: formatGigabytes(figure.totalBytes) }),
          percent: figure.percent,
        })
      : null;
  const nearlyFull = (figure: Figure<{ percent: number }>) => figure.kind === 'value' && isNearlyFull(figure.percent);

  const disk = percentOf(latest?.disk ?? null);
  const memory = percentOf(latest?.memory ?? null);
  const swap = percentOf(latest?.swap ?? null);
  const temperature = temperatureOf(latest);
  const load = loadOf(latest);
  const percentLabel = (percent: number) => t('threshold-percent', { percent });
  const loadValues = series((point) => (point.loadPerCore === null ? null : floorTenth(point.loadPerCore)));
  const loadMax = Math.max(LOAD_BAR_SCALE, ...loadValues.map((point) => point.value ?? 0));
  const common = { from, to, bucketMs };

  return (
    <div className={GRID}>
      <HistoryCard
        {...common}
        title={t('disk')}
        points={series((point) => (point.diskRatio === null ? null : ratioPercent(point.diskRatio)))}
        yMax={100}
        threshold={{ value: FLEET_HEALTH_WARN_PERCENT }}
        formatValue={percentLabel}
        value={usedOfTotal(disk)}
        warning={nearlyFull(disk)}
      />
      <HistoryCard
        {...common}
        title={t('memory')}
        points={series((point) => (point.memoryRatio === null ? null : ratioPercent(point.memoryRatio)))}
        yMax={100}
        threshold={{ value: FLEET_HEALTH_WARN_PERCENT }}
        formatValue={percentLabel}
        value={usedOfTotal(memory)}
        warning={nearlyFull(memory)}
      />
      <HistoryCard
        {...common}
        title={t('swap')}
        points={series((point) => (point.swapRatio === null ? null : ratioPercent(point.swapRatio)))}
        yMax={100}
        formatValue={percentLabel}
        value={usedOfTotal(swap)}
        warning={false}
      />
      <HistoryCard
        {...common}
        title={t('temperature')}
        points={series((point) => (point.cpuTemperatureC === null ? null : Math.floor(point.cpuTemperatureC)))}
        yMax={100}
        threshold={{ value: limits.temperatureC.throttleAt }}
        formatValue={(degrees) => t('temperature-value', { degrees })}
        value={temperature.kind === 'value' ? t('temperature-value', { degrees: temperature.degrees }) : null}
        warning={temperature.kind === 'value' && temperature.overLimit}
      />
      <HistoryCard
        {...common}
        className="sm:col-span-2"
        title={t('load')}
        points={loadValues}
        yMax={loadMax}
        threshold={{ value: limits.loadPerCore.throttleAt }}
        formatValue={(factor) => t('load-value', { factor: formatFactor(factor) })}
        value={load.kind === 'value' ? t('load-value', { factor: formatFactor(load.factor) }) : null}
        warning={load.kind === 'value' && load.overLimit}
      />
    </div>
  );
}
